import React, { useState } from 'react';
import { HeadphonesIcon, MessageCircle, Mail, CheckCircle, Clock, AlertCircle, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const SupportCenter = ({ i18n }) => {
  const [tickets, setTickets] = useState([
    {
      id: 1,
      user: 'Ahmad Ali',
      email: 'ahmad@example.com',
      subject: 'Cannot access lessons',
      message: 'I paid for subscription but still cannot access premium lessons',
      status: 'open',
      priority: 'high',
      date: '2024-01-15 10:30',
      replies: []
    },
    {
      id: 2,
      user: 'Sara Mohammed',
      email: 'sara@example.com',
      subject: 'Questions about subscription',
      message: 'What is the difference between monthly and yearly plans?',
      status: 'in_progress',
      priority: 'medium',
      date: '2024-01-14 15:45',
      replies: []
    },
    {
      id: 3,
      user: 'Omar Hassan',
      email: 'omar@example.com',
      subject: 'App not loading',
      message: 'The website is very slow and sometimes does not load',
      status: 'closed',
      priority: 'low',
      date: '2024-01-13 09:20',
      replies: []
    }
  ]);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const getStatusColor = (status) => {
    switch(status) {
      case 'open': return 'bg-red-100 text-red-700';
      case 'in_progress': return 'bg-amber-100 text-amber-700';
      case 'closed': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-amber-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-slate-500';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'open': return AlertCircle;
      case 'in_progress': return Clock;
      case 'closed': return CheckCircle;
      default: return MessageCircle;
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setModalOpen(true);
    setReplyText('');
  };

  const handleSendReply = () => {
    if (!replyText.trim()) {
      toast.error(i18n.language === 'ar' ? 'الرجاء كتابة رد' : 'Please write a reply');
      return;
    }

    const updatedTicket = {
      ...selectedTicket,
      replies: [...selectedTicket.replies, {
        text: replyText,
        date: new Date().toLocaleString(),
        from: 'admin'
      }],
      status: 'in_progress'
    };

    setTickets(tickets.map(t => t.id === selectedTicket.id ? updatedTicket : t));
    setSelectedTicket(updatedTicket);
    setReplyText('');
    toast.success(i18n.language === 'ar' ? 'تم إرسال الرد!' : 'Reply sent!');
  };

  const handleCloseTicket = () => {
    setTickets(tickets.map(t => 
      t.id === selectedTicket.id ? { ...t, status: 'closed' } : t
    ));
    setModalOpen(false);
    toast.success(i18n.language === 'ar' ? 'تم إغلاق التذكرة' : 'Ticket closed');
  };

  const filteredTickets = filterStatus === 'all' 
    ? tickets 
    : tickets.filter(t => t.status === filterStatus);

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in_progress').length,
    closed: tickets.filter(t => t.status === 'closed').length
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mb-6">
          <HeadphonesIcon className="w-7 h-7 text-teal-600" />
          {i18n.language === 'ar' ? 'مركز الدعم' : 'Support Center'}
        </h3>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
            <div className="text-2xl font-bold text-blue-700">{stats.total}</div>
            <div className="text-sm text-blue-600 mt-1">{i18n.language === 'ar' ? 'إجمالي التذاكر' : 'Total Tickets'}</div>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
            <div className="text-2xl font-bold text-red-700">{stats.open}</div>
            <div className="text-sm text-red-600 mt-1">{i18n.language === 'ar' ? 'مفتوحة' : 'Open'}</div>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4 border border-amber-200">
            <div className="text-2xl font-bold text-amber-700">{stats.inProgress}</div>
            <div className="text-sm text-amber-600 mt-1">{i18n.language === 'ar' ? 'قيد المعالجة' : 'In Progress'}</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
            <div className="text-2xl font-bold text-green-700">{stats.closed}</div>
            <div className="text-sm text-green-600 mt-1">{i18n.language === 'ar' ? 'مغلقة' : 'Closed'}</div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-4 border-b border-slate-200 pb-4">
          {[
            { value: 'all', label: i18n.language === 'ar' ? 'الكل' : 'All' },
            { value: 'open', label: i18n.language === 'ar' ? 'مفتوحة' : 'Open' },
            { value: 'in_progress', label: i18n.language === 'ar' ? 'قيد المعالجة' : 'In Progress' },
            { value: 'closed', label: i18n.language === 'ar' ? 'مغلقة' : 'Closed' }
          ].map(filter => (
            <Button
              key={filter.value}
              variant={filterStatus === filter.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus(filter.value)}
            >
              {filter.label}
            </Button>
          ))}
        </div>

        {/* Tickets List */}
        <div className="space-y-3">
          {filteredTickets.map((ticket) => {
            const StatusIcon = getStatusIcon(ticket.status);
            return (
              <div
                key={ticket.id}
                onClick={() => handleViewTicket(ticket)}
                className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-all border border-slate-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`w-2 h-2 rounded-full mt-2 ${getPriorityColor(ticket.priority)}`}></div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-slate-900">{ticket.subject}</h4>
                        <Badge className={getStatusColor(ticket.status)}>
                          <StatusIcon className="w-3 h-3 me-1" />
                          {i18n.language === 'ar' 
                            ? (ticket.status === 'open' ? 'مفتوحة' : ticket.status === 'in_progress' ? 'قيد المعالجة' : 'مغلقة')
                            : ticket.status.replace('_', ' ')
                          }
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-1">{ticket.message}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                        <span>{ticket.user}</span>
                        <span>•</span>
                        <span>{ticket.email}</span>
                        <span>•</span>
                        <span>{ticket.date}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredTickets.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <MessageCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>{i18n.language === 'ar' ? 'لا توجد تذاكر' : 'No tickets found'}</p>
          </div>
        )}
      </div>

      {/* Ticket Detail Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-teal-600" />
              {selectedTicket?.subject}
            </DialogTitle>
          </DialogHeader>

          {selectedTicket && (
            <div className="space-y-4 mt-4">
              {/* Ticket Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-semibold text-slate-900">{selectedTicket.user}</div>
                    <div className="text-sm text-slate-500">{selectedTicket.email}</div>
                  </div>
                  <div className="text-right">
                    <Badge className={getStatusColor(selectedTicket.status)}>
                      {i18n.language === 'ar' 
                        ? (selectedTicket.status === 'open' ? 'مفتوحة' : selectedTicket.status === 'in_progress' ? 'قيد المعالجة' : 'مغلقة')
                        : selectedTicket.status.replace('_', ' ')
                      }
                    </Badge>
                    <div className="text-xs text-slate-500 mt-1">{selectedTicket.date}</div>
                  </div>
                </div>
                <div className="text-sm text-slate-700">{selectedTicket.message}</div>
              </div>

              {/* Replies */}
              {selectedTicket.replies.length > 0 && (
                <div className="space-y-3">
                  <h5 className="font-semibold text-slate-700">{i18n.language === 'ar' ? 'الردود' : 'Replies'}</h5>
                  {selectedTicket.replies.map((reply, idx) => (
                    <div key={idx} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-blue-700">
                          {i18n.language === 'ar' ? 'الإدارة' : 'Admin'}
                        </span>
                        <span className="text-xs text-blue-600">{reply.date}</span>
                      </div>
                      <p className="text-sm text-slate-700">{reply.text}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply Form */}
              {selectedTicket.status !== 'closed' && (
                <div>
                  <h5 className="font-semibold text-slate-700 mb-2">{i18n.language === 'ar' ? 'رد على التذكرة' : 'Reply to Ticket'}</h5>
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={i18n.language === 'ar' ? 'اكتب ردك هنا...' : 'Write your reply here...'}
                    rows={4}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              {i18n.language === 'ar' ? 'إغلاق' : 'Close'}
            </Button>
            {selectedTicket?.status !== 'closed' && (
              <>
                <Button onClick={handleSendReply} className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600">
                  <Send className="w-4 h-4 me-2" />
                  {i18n.language === 'ar' ? 'إرسال الرد' : 'Send Reply'}
                </Button>
                <Button onClick={handleCloseTicket} variant="outline" className="text-green-600 hover:bg-green-50">
                  <CheckCircle className="w-4 h-4 me-2" />
                  {i18n.language === 'ar' ? 'إغلاق التذكرة' : 'Close Ticket'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportCenter;